// Main view for organizing projects and backlog.
// Geredesignd naar prototype-mui/OrganizePreview.jsx — master-detail split
// met Active/Backlog/Inbox tabs en InboxView voor triage.
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDroppable } from '@dnd-kit/core';
import { Box } from '@mui/material';
import { useT } from '@/i18n/useT';
import { SectionTabs } from '@/components/ui/SharedComponents';
import InboxView from '@/features/organize/components/InboxView';
import OrganizeActiveBacklogView from '@/features/organize/components/OrganizeActiveBacklogView';
import {
  useTasksContext,
  useTaskMutationsContext,
} from '@/features/tasks';
import { useOrganizeDragAndDrop } from '@/features/organize/hooks/useOrganizeDragAndDrop.js';
import { useOrganizeNavigation } from '@/features/organize/hooks/use-organize-navigation.js';
import { useProjectStates } from '@/features/tasks/hooks/status/use-project-states.js';
import useViewSpecificTasks from '@/features/tasks/hooks/list/use-list-view-tasks.js';
import { getInboxTabCount } from '@/features/organize/utils/inboxTriageUtils';
import { APP_PAGE_FILL_SX } from '@/components/layout/appViewport';
import OrganizeIntroHints from '@/features/organize/components/OrganizeIntroHints';
import InboxIntroHints from '@/features/organize/components/InboxIntroHints';
import { OnboardingHintQueueProvider } from '@/features/onboarding/context/OnboardingHintQueueContext';

const ORG_TAB_KEYS = ['active', 'backlog', 'inbox'];

/* ── OrganizePage ──────────────────────────────────── */
const OrganizePage = () => {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    return Math.max(
      0,
      ORG_TAB_KEYS.indexOf(searchParams.get('tab') || 'active'),
    );
  });
  const [inboxContextPanelOpen, setInboxContextPanelOpen] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab') || 'active';
    const idx = ORG_TAB_KEYS.indexOf(tab);
    if (idx >= 0) setActiveTab(idx);
  }, [searchParams]);

  const {
    allTasks,
    filteredTasks: contextFilteredTasks,
    loading,
    user,
    duplicateOrderKeyTaskIds,
    projectFilter,
    contextFilter,
  } = useTasksContext();
  // Same filter-active check as TodayViewContent / AppBarHeader (project + context chips).
  const hasActiveFilter =
    (projectFilter?.length || 0) > 0 || (contextFilter?.length || 0) > 0;
  const { updateTask, deleteTask } = useTaskMutationsContext();

  const {
    selectedTaskIds,
    selectedTask,
    isBulkEditModalOpen,
    setIsBulkEditModalOpen,
    handleSelectionChange,
    handleCloseDetail,
  } = useOrganizeNavigation(allTasks, loading);

  const { plannedParentIds, waitingForParentIds } = useProjectStates();
  const processedTasks = useViewSpecificTasks(contextFilteredTasks, 'organize');
  const {
    activeItem,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    sensors,
  } = useOrganizeDragAndDrop(allTasks, user);

  const { setNodeRef } = useDroppable({
    id: 'master-list-drop-zone',
    data: { type: 'master-list' },
  });

  const activeCount = useMemo(
    () =>
      processedTasks
        ? processedTasks.filter(
            (task) =>
              !task.completed && !task.parentId && task.status !== 'Backlog',
          ).length
        : 0,
    [processedTasks],
  );

  const backlogCount = useMemo(
    () =>
      processedTasks
        ? processedTasks.filter(
            (task) =>
              !task.completed && !task.parentId && task.status === 'Backlog',
          ).length
        : 0,
    [processedTasks],
  );

  const inboxCount = useMemo(() => getInboxTabCount(allTasks), [allTasks]);

  const ORG_TABS = [
    { label: t('organize.tabActive', 'Active'), count: activeCount },
    { label: t('organize.tabBacklog', 'Backlog'), count: backlogCount },
    { label: t('organize.tabInbox', 'Inbox'), count: inboxCount },
  ];

  const isInbox = ORG_TAB_KEYS[activeTab] === 'inbox';

  const masterListTasks = useMemo(() => {
    if (loading || !processedTasks) return [];
    const wantsBacklog = ORG_TAB_KEYS[activeTab] === 'backlog';
    return processedTasks.filter(
      (task) =>
        !task.completed &&
        !task.parentId &&
        (task.status === 'Backlog') === wantsBacklog,
    );
  }, [processedTasks, loading, activeTab]);

  const handleTabChange = (_, v) => {
    setActiveTab(v);
    if (ORG_TAB_KEYS[v] !== 'inbox') {
      setInboxContextPanelOpen(false);
    }
    setSearchParams({ tab: ORG_TAB_KEYS[v] }, { replace: true });
  };
  const handleTaskDelete = async (id) => {
    await deleteTask(id);
    handleCloseDetail();
  };

  const isDetailVisible = selectedTask !== null && selectedTask !== undefined;

  if (loading) {
    return <Box sx={{ p: 3 }}>{t('common.loading', 'Loading...')}</Box>;
  }

  const activeTabKey = ORG_TAB_KEYS[activeTab];

  return (
    <OnboardingHintQueueProvider enabled>
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          ...APP_PAGE_FILL_SX,
        }}
      >
        <SectionTabs
          tabs={ORG_TABS}
          value={activeTab}
          onChange={handleTabChange}
        />

        <OrganizeIntroHints activeTabKey={activeTabKey} userId={user?.uid} />
        {isInbox ? (
          <InboxIntroHints
            userId={user?.uid}
            isContextPanelOpen={inboxContextPanelOpen}
          />
        ) : null}

        {isInbox ? (
          <InboxView onContextPanelOpenChange={setInboxContextPanelOpen} />
        ) : (
          <OrganizeActiveBacklogView
            sensors={sensors}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleDragCancel={handleDragCancel}
            setNodeRef={setNodeRef}
            masterListTasks={masterListTasks}
            selectedTaskIds={selectedTaskIds}
            handleSelectionChange={handleSelectionChange}
            duplicateOrderKeyTaskIds={duplicateOrderKeyTaskIds}
            plannedParentIds={plannedParentIds}
            waitingForParentIds={waitingForParentIds}
            activeTabKey={activeTabKey}
            hasActiveFilter={hasActiveFilter}
            selectedTask={selectedTask}
            processedTasks={processedTasks}
            handleCloseDetail={handleCloseDetail}
            updateTask={updateTask}
            handleTaskDelete={handleTaskDelete}
            isDetailVisible={isDetailVisible}
            activeItem={activeItem}
            isBulkEditModalOpen={isBulkEditModalOpen}
            setIsBulkEditModalOpen={setIsBulkEditModalOpen}
          />
        )}
      </Box>
    </OnboardingHintQueueProvider>
  );
};

export default OrganizePage;
